/**
 * Browser-style header tabs, as NAVIGATION (not keep-alive).
 *
 * Mirrors the design mock's tab helpers (`weco2/shell.jsx`: docKeyOf,
 * sanitizeNav, loadTabs, selectTab, closeTab) but every tab is a pointer at a
 * URL rather than a mounted screen: switching a tab navigates the router to
 * that tab's last URL, and the module mounts fresh.
 *
 * Identity
 *   - one tab per module (keyed by route id), except
 *   - Write / Present / Grid, which get one tab per document, keyed by the
 *     document id in the route (`/write/:id`, `/present/:id`, `/grid/:id`).
 *     The module's own file list (`/write`) is a separate tab with docId null.
 *
 * Order is most-recently-used: whichever tab becomes active is pulled to the
 * front (leftmost), exactly like the mock's selectTab / onNavigate.
 *
 * Everything here is pure so it can be unit-tested without a router.
 */

export interface ShellTab {
  /** Stable key for React + focus management. */
  key: string;
  /** Route id from routes.ts (or a shell page id such as "profile"). */
  routeId: string;
  /** Document id for Write / Present / Grid editor tabs; null otherwise. */
  docId: string | null;
  /** The last URL (pathname + search + hash) visited inside this tab. */
  url: string;
  /** Last known document title, persisted so a refresh can show it at once. */
  title?: string;
}

export interface TabsState {
  tabs: ShellTab[];
  activeKey: string | null;
}

export interface TabTarget {
  routeId: string;
  docId: string | null;
}

/** Minimal shape of a route the resolver needs. */
export interface TabRouteRef {
  id: string;
  path: string;
}

/** Apps that open one tab per document. */
export const DOC_ROUTE_IDS: ReadonlySet<string> = new Set(["write", "present", "grid"]);

/** Shell-only pages (main.tsx) that behave like singleton module tabs. */
export const SHELL_PAGE_IDS: ReadonlySet<string> = new Set([
  "profile",
  "account",
  "preferences",
  "suite-status",
]);

/**
 * Second path segments inside a doc app that are NOT a document id.
 * `doc` is Write's legacy `/write/doc/:id` redirect (the id is the third
 * segment); `import-from-vault` is a one-shot hand-off that navigates on to
 * the new document by itself.
 */
const DOC_RESERVED_SEGMENTS: ReadonlySet<string> = new Set(["doc", "import-from-vault"]);

/**
 * Query params that trigger an action on arrival (start a conversation from a
 * prompt, join a room, pick up a knock call). They must fire once, never again
 * when a persisted tab is reopened or the page is refreshed — the same job the
 * mock's sanitizeNav does for `openCreate`.
 */
export const ONE_SHOT_PARAMS: ReadonlyArray<string> = [
  "prompt",
  "attachment",
  "room",
  "call",
  "token",
  "openCreate",
  /* Planner guest hover → Mail compose with a prefilled To. */
  "compose",
  "to",
];

export const HOME_ROUTE_ID = "dashboard";

/** Which tab a URL belongs to. Returns null for paths no tab should claim. */
export function resolveTabTarget(
  pathname: string,
  routes: ReadonlyArray<TabRouteRef>,
): TabTarget | null {
  const segments = pathname.split("/").filter(Boolean).map(safeDecode);
  const first = segments[0] ?? "";
  if (SHELL_PAGE_IDS.has(first)) return { routeId: first, docId: null };

  const route = routes.find((r) => r.path !== "/" && r.path === `/${first}`);
  if (!route) {
    // "/" and the assistant's own sub-routes (/c/:id, /chats, /new) all
    // belong to the Intelligence Hub, which is mounted at "/*".
    const home = routes.find((r) => r.path === "/");
    return home ? { routeId: home.id, docId: null } : null;
  }

  if (!DOC_ROUTE_IDS.has(route.id)) return { routeId: route.id, docId: null };

  const second = segments[1];
  if (!second) return { routeId: route.id, docId: null };
  if (second === "doc") {
    return { routeId: route.id, docId: segments[2] ?? null };
  }
  if (DOC_RESERVED_SEGMENTS.has(second)) return { routeId: route.id, docId: null };
  return { routeId: route.id, docId: second };
}

function safeDecode(seg: string): string {
  try {
    return decodeURIComponent(seg);
  } catch {
    return seg;
  }
}

export function sameTarget(tab: Pick<ShellTab, "routeId" | "docId">, target: TabTarget): boolean {
  return tab.routeId === target.routeId && (tab.docId ?? null) === (target.docId ?? null);
}

/**
 * A URL that is safe to come back to: one-shot params stripped, and one-shot
 * routes (vault import hand-offs) collapsed to the module root.
 */
export function sanitizeTabUrl(url: string): string {
  let parsed: URL;
  try {
    parsed = new URL(url, "http://shell.local");
  } catch {
    return "/";
  }
  const segments = parsed.pathname.split("/").filter(Boolean);
  if (segments[1] === "import-from-vault") {
    return `/${segments[0]}`;
  }
  for (const p of ONE_SHOT_PARAMS) parsed.searchParams.delete(p);
  const search = parsed.searchParams.toString();
  return `${parsed.pathname}${search ? `?${search}` : ""}${parsed.hash}`;
}

export function newTabKey(): string {
  return Math.random().toString(36).slice(2, 10);
}

function toFront(tabs: ShellTab[], tab: ShellTab): ShellTab[] {
  return [tab, ...tabs.filter((t) => t.key !== tab.key)];
}

/**
 * The router arrived at `url`. Focus (and record the URL on) the matching tab,
 * or open a new one at the front. Re-arriving at the active tab only updates
 * its last URL — it never reorders.
 */
export function visitUrl(
  state: TabsState,
  target: TabTarget,
  url: string,
  makeKey: () => string = newTabKey,
): TabsState {
  const clean = sanitizeTabUrl(url);
  const existing = state.tabs.find((t) => sameTarget(t, target));
  if (existing) {
    const updated = existing.url === clean ? existing : { ...existing, url: clean };
    if (state.activeKey === existing.key && state.tabs[0]?.key === existing.key) {
      if (updated === existing) return state;
      return { ...state, tabs: state.tabs.map((t) => (t.key === existing.key ? updated : t)) };
    }
    return { tabs: toFront(state.tabs, updated), activeKey: existing.key };
  }
  const tab: ShellTab = { key: makeKey(), routeId: target.routeId, docId: target.docId, url: clean };
  return { tabs: [tab, ...state.tabs], activeKey: tab.key };
}

/** Make a tab active and pull it to the front. Returns the state unchanged for unknown keys. */
export function selectTab(state: TabsState, key: string): TabsState {
  const tab = state.tabs.find((t) => t.key === key);
  if (!tab) return state;
  if (state.activeKey === key && state.tabs[0]?.key === key) return state;
  return { tabs: toFront(state.tabs, tab), activeKey: key };
}

export interface CloseResult {
  state: TabsState;
  /** Where the router should go next, when the active tab changed. */
  navigateTo: string | null;
}

/**
 * Close a tab. Closing the active tab focuses its neighbour (the tab that
 * slides into its slot, else the one before it) and pulls it to the front;
 * closing the last tab opens a fresh home tab at `homeUrl`.
 */
export function closeTab(
  state: TabsState,
  key: string,
  homeUrl: string,
  homeTarget: TabTarget,
  makeKey: () => string = newTabKey,
): CloseResult {
  const idx = state.tabs.findIndex((t) => t.key === key);
  if (idx === -1) return { state, navigateTo: null };
  const next = state.tabs.filter((t) => t.key !== key);
  if (next.length === 0) {
    const home: ShellTab = { key: makeKey(), routeId: homeTarget.routeId, docId: homeTarget.docId, url: homeUrl };
    return { state: { tabs: [home], activeKey: home.key }, navigateTo: homeUrl };
  }
  if (key !== state.activeKey) {
    return { state: { tabs: next, activeKey: state.activeKey }, navigateTo: null };
  }
  const neighbour = next[Math.min(idx, next.length - 1)];
  return {
    state: { tabs: toFront(next, neighbour), activeKey: neighbour.key },
    navigateTo: neighbour.url,
  };
}

/**
 * Drop tabs the user can no longer open (app disabled for the org, RBAC role
 * change, admin rights revoked). When the active tab goes, the first
 * remaining tab takes over.
 */
export function pruneTabs(
  state: TabsState,
  isAllowed: (routeId: string) => boolean,
): { state: TabsState; navigateTo: string | null } {
  const kept = state.tabs.filter((t) => isAllowed(t.routeId));
  if (kept.length === state.tabs.length) return { state, navigateTo: null };
  const activeStillThere = kept.some((t) => t.key === state.activeKey);
  if (activeStillThere) return { state: { tabs: kept, activeKey: state.activeKey }, navigateTo: null };
  const nextActive = kept[0] ?? null;
  return {
    state: { tabs: kept, activeKey: nextActive?.key ?? null },
    navigateTo: nextActive?.url ?? null,
  };
}

/** Record a document title on its tab (no-op when unchanged). */
export function setTabTitle(state: TabsState, key: string, title: string | undefined): TabsState {
  const tab = state.tabs.find((t) => t.key === key);
  if (!tab || tab.title === title) return state;
  return { ...state, tabs: state.tabs.map((t) => (t.key === key ? { ...t, title } : t)) };
}

// ---------------------------------------------------------------- storage

const STORAGE_PREFIX = "weco.shell.tabs.v1";

/**
 * Per signed-in user, so one account's open documents never surface as tabs
 * for the next person on a shared browser.
 */
export function tabsStorageKey(userId: string): string {
  return `${STORAGE_PREFIX}:${userId}`;
}

interface StoredTabs {
  tabs: Array<Partial<ShellTab>>;
  activeKey?: string | null;
}

function isStoredTab(t: Partial<ShellTab>): t is ShellTab {
  return typeof t?.routeId === "string" && typeof t?.url === "string";
}

/**
 * Restore the persisted tabs for `userId`. Tabs the user may no longer open
 * are dropped, one-shot params are stripped, and the active tab is pulled to
 * the front. Returns an empty state when nothing usable is stored.
 */
export function loadTabs(
  storage: Pick<Storage, "getItem"> | null,
  userId: string | null | undefined,
  isAllowed: (routeId: string) => boolean,
): TabsState {
  const empty: TabsState = { tabs: [], activeKey: null };
  if (!storage || !userId) return empty;
  let raw: StoredTabs | null = null;
  try {
    raw = JSON.parse(storage.getItem(tabsStorageKey(userId)) ?? "null") as StoredTabs | null;
  } catch {
    return empty;
  }
  if (!raw || !Array.isArray(raw.tabs)) return empty;
  const seen = new Set<string>();
  const tabs: ShellTab[] = [];
  for (const t of raw.tabs) {
    if (!isStoredTab(t) || !isAllowed(t.routeId)) continue;
    const docId = typeof t.docId === "string" ? t.docId : null;
    const identity = `${t.routeId}::${docId ?? ""}`;
    if (seen.has(identity)) continue;
    seen.add(identity);
    tabs.push({
      key: typeof t.key === "string" && t.key ? t.key : newTabKey(),
      routeId: t.routeId,
      docId,
      url: sanitizeTabUrl(t.url),
      ...(typeof t.title === "string" && t.title ? { title: t.title } : {}),
    });
  }
  if (tabs.length === 0) return empty;
  const active = tabs.find((t) => t.key === raw!.activeKey) ?? tabs[0];
  return { tabs: toFront(tabs, active), activeKey: active.key };
}

export function saveTabs(
  storage: Pick<Storage, "setItem"> | null,
  userId: string | null | undefined,
  state: TabsState,
): void {
  if (!storage || !userId) return;
  try {
    storage.setItem(
      tabsStorageKey(userId),
      JSON.stringify({
        tabs: state.tabs.map(({ key, routeId, docId, url, title }) => ({
          key,
          routeId,
          docId,
          url: sanitizeTabUrl(url),
          ...(title ? { title } : {}),
        })),
        activeKey: state.activeKey,
      }),
    );
  } catch {
    // Quota exceeded / storage blocked: tabs just won't survive a refresh.
  }
}
